import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import api from "../lib/api.ts";
import leftbgimg from "../assets/leftbg.jpg";
import bgimg from "../assets/loginbg.png";

const ResetPassword = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const token = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromQuery = qs.get("token");
    if (fromQuery) return fromQuery;
    return params?.token || "";
  }, [location.search, params?.token]);

  // Verify token on mount - backend returns { success: true, valid: true } if token is valid
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setVerifyMessage("Reset link is invalid or expired.");
        setIsVerifying(false);
        return;
      }

      setIsVerifying(true);
      try {
        // POST to /auth/reset-password with only token (no password) to verify
        const res = await api.post(`/auth/reset-password`, { token });
        const data = res?.data;

        // Backend returns { success: true, message: "Token is valid", valid: true } for valid tokens
        if (data?.success === true && data?.valid === true) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          setVerifyMessage(
            data?.message || "Reset link is invalid or expired."
          );
        }
      } catch (e) {
        const data = e?.response?.data;
        const msg =
          (typeof data === "string" && data) ||
          data?.message ||
          data?.error ||
          "Reset link is invalid or expired.";
        setIsTokenValid(false);
        setVerifyMessage(msg);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: { password: "", confirmPassword: "" } });

  const onSubmit = async ({ password, confirmPassword }) => {
    if (!token) {
      showError("Reset link is invalid or expired.");
      return;
    }
    setIsLoading(true);
    try {
      await api.post(`/auth/reset-password`, {
        token,
        password,
        confirmPassword,
      });
      showSuccess("Your password has been reset. You can now sign in.");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (e) {
      let errorMessage = "Could not reset password. Please try the link again.";
      const data = e?.response?.data;
      if (data) {
        if (typeof data === "string") {
          errorMessage = data;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (Array.isArray(data.errors)) {
          errorMessage = data.errors.join(", ");
        } else if (data.errors && typeof data.errors === "object") {
          errorMessage = Object.values(data.errors).flat().join(", ");
        } else if (Array.isArray(data.details)) {
          errorMessage = data.details.join(", ");
        } else if (data.validation && typeof data.validation === "object") {
          errorMessage = Object.values(data.validation).flat().join(", ");
        }
      } else if (e?.message) {
        errorMessage = e.message;
      }
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const passwordValue = watch("password");

  const handleResend = async () => {
    try {
      setResendLoading(true);
      await api.post(`/auth/forgot-password`, { token });
      showSuccess("A new reset link has been sent to your email.");
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.error ||
        "Failed to send reset link. Please try again.";
      showError(msg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      className='min-h-[94vh] p-2 flex items-center justify-center bg-cover bg-center'
      style={{ backgroundImage: `url(${bgimg})` }}
    >
      <div className='bg-white/20 backdrop-blur-lg w-full max-w-7xl rounded-3xl shadow-2xl border-[3px] border-white relative overflow-hidden'>
        <div className='flex flex-col md:flex-row min-h-[800px] relative'>
          {/* Left side - image panel */}
          <div className='md:w-1/2 relative text-white p-10 overflow-hidden'>
            <div
              className='absolute inset-0 bg-cover bg-center'
              style={{ backgroundImage: `url(${leftbgimg})` }}
            >
              <div className='absolute inset-0 bg-black opacity-20'></div>
            </div>
            <div className='relative z-10 text-center md:pt-20'>
              <h2 className='text-[30px] md:text-[60px] font-semibold mb-2'>
                Reset Password
              </h2>
              <p className='text-lg md:text-[20px] text-white/90 mb-6'>
                Create a new password to access your account
              </p>
            </div>
          </div>

          {/* Right side - form or verification state */}
          <div className='md:w-1/2 bg-white/70 backdrop-blur-md p-10 flex items-center justify-center  overflow-y-auto max-h-[800px]'>
            {isVerifying ? (
              <div className='w-full max-w-md text-center text-[#13131399]'>
                Verifying link...
              </div>
            ) : !isTokenValid ? (
              <div className='w-full max-w-md'>
                <h3 className='text-2xl md:text-[28px] font-bold mb-2 text-[#131313]'>
                  Link invalid or expired
                </h3>
                <p className='text-[#13131399] mb-6'>{verifyMessage}</p>
                <button
                  type='button'
                  onClick={handleResend}
                  disabled={resendLoading}
                  className='inline-block px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-60'
                >
                  {resendLoading ? "Sending..." : "Request a new reset link"}
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className='w-full max-w-md'
              >
                <div className='space-y-3'>
                  <h3 className='text-2xl md:text-[28px] font-bold mb-1 text-[#131313]'>
                    Reset your password
                  </h3>
                  <p className='text-[#13131399] pb-6 text-sm md:text-[16px]'>
                    Enter your new password below.
                  </p>
                </div>

                <div className='mb-4'>
                  <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
                    New password
                  </label>
                  <input
                    type='password'
                    className={`w-full px-3 py-3 border ${
                      errors.password ? "border-red-400" : "border-[#13131399]"
                    } rounded-lg bg-white placeholder:text-text-[rgba(19,19,19,1)] focus:outline-none`}
                    {...register("password", {
                      required: "Please enter a new password.",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters.",
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                        message:
                          "Password must contain uppercase, lowercase and a number.",
                      },
                    })}
                  />
                  {errors.password && (
                    <p className='mt-1 text-sm text-red-500'>
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className='mb-6'>
                  <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
                    Confirm password
                  </label>
                  <input
                    type='password'
                    className={`w-full px-3 py-3 border ${
                      errors.confirmPassword
                        ? "border-red-400"
                        : "border-[#13131399]"
                    } rounded-lg bg-white placeholder:text-text-[rgba(19,19,19,1)] focus:outline-none`}
                    {...register("confirmPassword", {
                      required: "Please confirm your new password.",
                      validate: (v) =>
                        v === passwordValue || "Passwords do not match.",
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className='mt-1 text-sm text-red-500'>
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <div className='flex items-center justify-end gap-2'>
                  <a
                    href='/login'
                    className='px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50'
                  >
                    Cancel
                  </a>
                  <button
                    type='submit'
                    disabled={isLoading}
                    className='px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-60'
                  >
                    {isLoading ? "Saving..." : "Reset password"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
